import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { FaEye, FaEdit, FaPlus, FaTrash, FaFileUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import api from "../../api/axios";
import { ADMINS_AND_MANAGERS } from "../../constants/roles";
import { userHasAnyRole } from "../../utils/leadershipRoles";
import UnitModal from "./UnitModal";
import UnitQuickViewModal from "./UnitQuickViewModal";
import PropertyForm from "./PropertyForm";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal";
import { deleteProperty } from "../../redux/slices/propertySlice";
import MainLayout from "../layoutpage/MainLayout";
import Footer from "../../components/Footer";

const facilityText = (facilities) => {
  if (Array.isArray(facilities)) return facilities.join(", ");
  if (typeof facilities === "string" && facilities.length) return facilities;
  if (facilities && typeof facilities === "object") {
    return Object.values(facilities).join(", ");
  }
  return "NA";
};

const extractMeasurement = (unit) => {
  if (!unit?.measurement) return null;
  if (Array.isArray(unit.measurement)) {
    return unit.measurement.length ? unit.measurement[0] : null;
  }
  return unit.measurement;
};

const getUnitPhotoUrl = (unit) => {
  if (
    !unit?.photos ||
    !Array.isArray(unit.photos) ||
    unit.photos.length === 0
  ) {
    return null;
  }
  const photo = unit.photos.find((p) => p.is_primary) || unit.photos[0];
  if (photo.file_path.startsWith("http")) {
    return photo.file_path;
  }
  return `${api.defaults.baseURL}/${photo.file_path}`;
};

export default function PropertyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const dispatch = useDispatch();

  const [property, setProperty] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitsPage, setUnitsPage] = useState(1);
  const [unitsLastPage, setUnitsLastPage] = useState(1);
  const [unitsTotal, setUnitsTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const [owner, setOwner] = useState(null);
  const [manager, setManager] = useState(null);

  const [isAdminUser, setIsAdminUser] = useState(false);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [viewUnit, setViewUnit] = useState(null);
  const [showPropertyForm, setShowPropertyForm] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUnitDeleteModal, setShowUnitDeleteModal] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);

  // ================= SLIDER & LIGHTBOX =================
  const [activeSlide, setActiveSlide] = useState(0);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        setIsAdminUser(userHasAnyRole(parsed, ADMINS_AND_MANAGERS));
      } catch {}
    }
  }, []);

  const UNITS_PER_PAGE = 10;

  const loadProperty = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get(`/api/properties/${id}`);
      setProperty(data);
      if (data.owner_id) fetchOwner(data.owner_id);
      if (data.manager_id) fetchManager(data.manager_id);
    } catch (error) {
      toast.error("Failed to load property!");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [id]);

  const loadUnits = useCallback(
    async (page = 1) => {
      if (!id) return;
      setUnitsLoading(true);
      try {
        const { data } = await api.get(`/api/units`, {
          params: { property_id: id, page, per_page: UNITS_PER_PAGE },
        });
        setUnits(Array.isArray(data?.unit) ? data.unit : []);
        setUnitsPage(data?.current_page ?? 1);
        setUnitsLastPage(data?.last_page ?? 1);
        setUnitsTotal(data?.total ?? 0);
      } catch (error) {
        toast.error("Failed to load units!");
        setUnits([]);
      } finally {
        setUnitsLoading(false);
      }
    },
    [id]
  );

  const fetchOwner = async (ownerId) => {
    try {
      const res = await api.get(`/api/users/${ownerId}`);
      setOwner(res.data);
    } catch {
      console.log("Owner fetch failed");
    }
  };

  const fetchManager = async (managerId) => {
    try {
      const res = await api.get(`/api/users/${managerId}`);
      setManager(res.data);
    } catch {
      console.log("Manager fetch failed");
    }
  };

  useEffect(() => {
    loadProperty();
  }, [loadProperty]);

  useEffect(() => {
    if (property?.id) loadUnits(1);
  }, [property?.id, loadUnits]);

  // ================= AUTO SLIDER LOGIC =================
  const allPhotos = useMemo(() => {
    if (!property?.photos || !Array.isArray(property.photos)) return [];
    return property.photos.map((photo) => ({
      ...photo,
      url: photo.file_path.startsWith("http")
        ? photo.file_path
        : `${api.defaults.baseURL}/${photo.file_path}`,
    }));
  }, [property]);

  useEffect(() => {
    if (allPhotos.length <= 1) return;
    const timer = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % allPhotos.length);
    }, 4000); // 4 Second Auto-slide
    return () => clearInterval(timer);
  }, [allPhotos.length]);

  // Handle Lightbox Auto-slide separately or sync
  useEffect(() => {
    if (lightboxIndex === null || allPhotos.length === 0) return;
    const timer = setInterval(() => {
      setLightboxIndex((prev) => (prev + 1) % allPhotos.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [lightboxIndex, allPhotos.length]);

  // ================= HANDLERS =================
  const handleAddUnit = () => {
    setSelectedUnit(null);
    setShowUnitModal(true);
  };

  const handleEditUnit = (unit) => {
    setSelectedUnit(unit);
    setShowUnitModal(true);
  };

  const handleDeleteUnit = (unit) => {
    setUnitToDelete(unit);
    setShowUnitDeleteModal(true);
  };

  const handleUnitDeleteConfirm = async () => {
    if (!unitToDelete) return;
    try {
      await api.delete(`/api/units/${unitToDelete.id}`);
      toast.success("Unit deleted successfully");
      await loadProperty();
      const goToPage =
        units.length <= 1 && unitsPage > 1 ? unitsPage - 1 : unitsPage;
      loadUnits(goToPage);
      setShowUnitDeleteModal(false);
      setUnitToDelete(null);
    } catch {
      toast.error("Failed to delete unit");
    }
  };

  const handleUnitSaved = async () => {
    setShowUnitModal(false);
    setSelectedUnit(null);
    await loadProperty();
    loadUnits(unitsPage);
  };

  const handleEditProperty = () => setShowPropertyForm(true);
  const handleDeleteProperty = () => setShowDeleteModal(true);

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteProperty(property.id)).unwrap();
      toast.success("Property deleted successfully!");
      navigate("/properties");
    } catch (error) {
      toast.error("Failed to delete property!");
    } finally {
      setShowDeleteModal(false);
    }
  };

  const handlePropertySaved = async () => {
    setShowPropertyForm(false);
    await loadProperty();
  };

  if (loading) return <p>Loading...</p>;
  if (!property) return <p>No Property Found</p>;

  return (
    <>
      <div className="container-fluid mt-4">
        <button className="btn btn-secondary mb-3" onClick={() => navigate(-1)}>
          ⬅ Back
        </button>

        <div className="card shadow-sm mb-4">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h3 className="fw-bold mb-0">{property.name}</h3>
            {isAdminUser && (
              <div className="d-flex gap-2">
                <button
                  className="btn btn-outline-info btn-sm"
                  onClick={() =>
                    navigate(
                      `/documentManagement?property_id=${property.id}&type=property`
                    )
                  }
                  title="Manage Documents"
                >
                  <FaFileUpload className="me-1" /> Manage Documents
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleEditProperty}
                >
                  <FaEdit className="me-1" /> Edit
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={handleDeleteProperty}
                >
                  <FaTrash className="me-1" /> Delete
                </button>
              </div>
            )}
          </div>
          <div className="card-body">
            <div className="row g-4">
              {/* LEFT SIDE INFO */}
              <div className="col-md-7">
                <dl className="row mb-0">
                  <dt className="col-sm-4">Type</dt>
                  <dd className="col-sm-8">{property.type?.name || "—"}</dd>

                  <dt className="col-sm-4">Status</dt>
                  <dd className="col-sm-8">
                    {property.status?.status_type || "—"}
                  </dd>

                  <dt className="col-sm-4">Location</dt>
                  <dd className="col-sm-8">
                    {routeLocation?.state?.property?.location?.area || "NA"} <br />
                    Road: {routeLocation?.state?.property?.location?.road_number || "NA"} <br />
                    Block: {routeLocation?.state?.property?.location?.block || "NA"}
                  </dd>

                  <dt className="col-sm-4">Total Floors</dt>
                  <dd className="col-sm-8">{property.total_floors ?? "—"}</dd>

                  <dt className="col-sm-4">Total Flats (per floor)</dt>
                  <dd className="col-sm-8">8</dd>

                  <dt className="col-sm-4">Total Flats (From floor 2 to 15)</dt>
                  <dd className="col-sm-8">{property.total_units ?? 0}</dd>

                  <dt className="col-sm-4">Shops (Ground floor)</dt>
                  <dd className="col-sm-8">49</dd>

                  <dt className="col-sm-4">Shops (1st floor)</dt>
                  <dd className="col-sm-8">53</dd>

                  <dt className="col-sm-4">Land Area (Katha)</dt>
                  <dd className="col-sm-8">{property.area_sq_ft ?? "—"}</dd>
                </dl>

                {property.description && (
                  <>
                    <hr />
                    <span className="fw-bold">Description</span>
                    <p className="mt-2 text-muted">{property.description}</p>
                  </>
                )}
              </div>

              {/* RIGHT SIDE PHOTOS WITH AUTO/MANUAL SLIDER */}
              <div className="col-md-5">
                {allPhotos.length > 0 ? (
                  <div>
                    {/* Main Slider Area */}
                    <div 
                      className="mb-3 border rounded overflow-hidden position-relative"
                      style={{ height: "450px", backgroundColor: "#f8f9fa" }}
                    >
                      <img
                        src={allPhotos[activeSlide]?.url}
                        alt={property.name}
                        className="img-fluid w-100 h-100"
                        style={{ objectFit: "cover", cursor: "pointer" }}
                        onClick={() => setLightboxIndex(activeSlide)}
                      />

                      {/* Manual Slider Buttons */}
                      {allPhotos.length > 1 && (
                        <>
                          <button 
                            className="btn btn-dark btn-sm position-absolute top-50 start-0 translate-middle-y ms-2 opacity-75"
                            style={{ borderRadius: "50%", width: "40px", height: "40px", fontSize: "20px" }}
                            onClick={() => setActiveSlide((prev) => (prev - 1 + allPhotos.length) % allPhotos.length)}
                          >
                            ‹
                          </button>
                          <button 
                            className="btn btn-dark btn-sm position-absolute top-50 end-0 translate-middle-y me-2 opacity-75"
                            style={{ borderRadius: "50%", width: "40px", height: "40px", fontSize: "20px" }}
                            onClick={() => setActiveSlide((prev) => (prev + 1) % allPhotos.length)}
                          >
                            ›
                          </button>

                          {/* Dots Indicators */}
                          <div className="position-absolute bottom-0 start-50 translate-middle-x mb-2 d-flex gap-1">
                            {allPhotos.map((_, idx) => (
                              <div 
                                key={idx}
                                onClick={() => setActiveSlide(idx)}
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  backgroundColor: activeSlide === idx ? "#fff" : "rgba(255,255,255,0.5)",
                                  cursor: "pointer",
                                  boxShadow: "0 0 2px rgba(0,0,0,0.5)"
                                }}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>

                    {/* Thumbnails Gallery */}
                    {allPhotos.length > 1 && (
                      <div className="row g-2">
                        {allPhotos.slice(0, 4).map((photo, index) => (
                          <div
                            key={photo.id}
                            className={`col-3 ${activeSlide === index ? 'border border-primary border-3' : ''}`}
                            style={{ cursor: "pointer" }}
                            onClick={() => setActiveSlide(index)}
                          >
                            <img
                              src={photo.url}
                              alt="thumb"
                              className="img-fluid rounded"
                              style={{ height: "80px", width: "100%", objectFit: "cover" }}
                            />
                          </div>
                        ))}
                        {allPhotos.length > 4 && (
                          <div 
                            className="col-3 d-flex align-items-center justify-content-center bg-light rounded border"
                            style={{ cursor: "pointer", height: "80px" }}
                            onClick={() => setLightboxIndex(4)}
                          >
                            <span className="text-muted small">+{allPhotos.length - 4} more</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="border rounded overflow-hidden bg-light h-100 d-flex align-items-center justify-content-center" style={{ minHeight: "300px" }}>
                    <span className="text-muted">No Photos Available</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ================= LIGHTBOX MODAL ================= */}
        {lightboxIndex !== null && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              background: "rgba(0,0,0,0.92)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setLightboxIndex(null)}
          >
            <button
              style={{
                position: "absolute",
                left: 24,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "#fff",
                fontSize: 40,
                borderRadius: "50%",
                width: 60,
                height: 60,
                cursor: "pointer",
                zIndex: 10000,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev - 1 + allPhotos.length) % allPhotos.length);
              }}
            >
              ‹
            </button>

            <img
              src={allPhotos[lightboxIndex]?.url}
              alt="Lightbox"
              style={{
                maxHeight: "90vh",
                maxWidth: "90vw",
                objectFit: "contain",
                boxShadow: "0 8px 30px rgba(0,0,0,0.5)",
              }}
              onClick={(e) => e.stopPropagation()}
            />

            <button
              style={{
                position: "absolute",
                right: 24,
                top: "50%",
                transform: "translateY(-50%)",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                color: "#fff",
                fontSize: 40,
                borderRadius: "50%",
                width: 60,
                height: 60,
                cursor: "pointer",
                zIndex: 10000,
              }}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((prev) => (prev + 1) % allPhotos.length);
              }}
            >
              ›
            </button>

            <button
              style={{
                position: "absolute",
                top: 20,
                right: 20,
                background: "transparent",
                border: "none",
                color: "#fff",
                fontSize: 30,
                cursor: "pointer",
              }}
              onClick={() => setLightboxIndex(null)}
            >
              ✕
            </button>
          </div>
        )}

        {/* ================= OWNER / MANAGER CARD ================= */}
        <div className="card shadow-sm mb-4">
          <div className="card-body row">
            <div className="col-md-6">
              <h5 className="fw-bold mb-3">Owner Information</h5>
              {!owner ? (
                <p className="text-muted">No owner assigned</p>
              ) : (
                <ul className="list-group">
                  <li className="list-group-item">
                    <strong>{owner.name}</strong> <br />
                    Email: {owner.email} <br />
                    Phone: {owner.mobile_number ?? "N/A"}
                  </li>
                </ul>
              )}
            </div>
            <div className="col-md-6">
              <h5 className="fw-bold mb-3">Manager Information</h5>
              {!manager ? (
                <p className="text-muted">No manager assigned</p>
              ) : (
                <ul className="list-group">
                  <li className="list-group-item">
                    <strong>{manager.name}</strong> <br />
                    Email: {manager.email} <br />
                    Phone: {manager.mobile_number ?? "N/A"}
                  </li>
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* ================= UNITS SECTION ================= */}
        <div className="card shadow-sm">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h4 className="mb-0">Units</h4>
            {isAdminUser && (
              <button className="btn btn-primary btn-sm" onClick={handleAddUnit}>
                <FaPlus className="me-2" /> Add Unit
              </button>
            )}
          </div>

          <div className="card-body">
            {unitsLoading || refreshing ? (
              <p className="text-muted">Loading units...</p>
            ) : unitsTotal === 0 ? (
              <p className="text-muted">No units found for this property.</p>
            ) : (
              <>
                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead className="table-light">
                      <tr>
                        <th>Unit #</th>
                        <th>Image</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Purpose</th>
                        <th>MO</th>
                        <th>MM</th>
                        <th>Floor</th>
                        <th>Price</th>
                        <th>Rent</th>
                        <th>Area</th>
                        <th>Katha</th>
                        <th>Bigha</th>
                        <th>Acre</th>
                        <th>Facilities</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {units.map((unit) => {
                        const m = extractMeasurement(unit);
                        return (
                          <tr key={unit.id}>
                            <td>{unit.unit_number || "—"}</td>
                            <td>
                              {getUnitPhotoUrl(unit) ? (
                                <img
                                  src={getUnitPhotoUrl(unit)}
                                  alt={unit.unit_number}
                                  style={{ height: 50, width: 60, objectFit: "cover" }}
                                />
                              ) : (
                                <span className="text-muted small">No photo</span>
                              )}
                            </td>
                            <td>{unit.type?.name || "—"}</td>
                            <td>{unit.status?.status_type || "—"}</td>
                            <td>{unit.purpose || "—"}</td>
                            <td>{unit.marketing_officer?.name || "Unassigned"}</td>
                            <td>{unit.marketing_manager?.name || "Unassigned"}</td>
                            <td>{unit.floor ?? "—"}</td>
                            <td>{unit.price ? `৳ ${Number(unit.price).toLocaleString()}` : "—"}</td>
                            <td>{unit.rent_amount ? `৳ ${Number(unit.rent_amount).toLocaleString()}` : "—"}</td>
                            <td>{m?.area_sq_ft ?? "—"}</td>
                            <td>{m?.katha ?? "—"}</td>
                            <td>{m?.bigha ?? "—"}</td>
                            <td>{m?.acre ?? "—"}</td>
                            <td>{facilityText(unit.facilities)}</td>
                            <td>
                              <div className="d-flex justify-content-end gap-2 flex-wrap">
                                <button className="btn btn-outline-secondary btn-sm" onClick={() => setViewUnit(unit)}>
                                  <FaEye />
                                </button>
                                {isAdminUser && (
                                  <>
                                    <button 
                                      className="btn btn-outline-info btn-sm"
                                      onClick={() => navigate(`/documentManagement?unit_id=${unit.id}&type=unit`)}
                                    >
                                      <FaFileUpload />
                                    </button>
                                    <button className="btn btn-outline-primary btn-sm" onClick={() => handleEditUnit(unit)}>
                                      <FaEdit />
                                    </button>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteUnit(unit)}>
                                      <FaTrash />
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {unitsTotal > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <span className="text-muted small">
                      Showing {(unitsPage - 1) * UNITS_PER_PAGE + 1} – {Math.min(unitsPage * UNITS_PER_PAGE, unitsTotal)} of {unitsTotal} units
                    </span>
                    <div className="d-flex gap-1 align-items-center">
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        disabled={unitsPage <= 1} 
                        onClick={() => loadUnits(unitsPage - 1)}
                      >
                        Previous
                      </button>
                      <span className="px-2 small">Page {unitsPage} of {unitsLastPage}</span>
                      <button 
                        className="btn btn-sm btn-outline-secondary" 
                        disabled={unitsPage >= unitsLastPage} 
                        onClick={() => loadUnits(unitsPage + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* MODALS */}
        {showUnitModal && (
          <UnitModal
            show={showUnitModal}
            property={property}
            propertyId={property.id}
            unit={selectedUnit}
            onClose={() => setShowUnitModal(false)}
            onSaved={handleUnitSaved}
          />
        )}
        {viewUnit && (
          <UnitQuickViewModal show={!!viewUnit} unit={viewUnit} onClose={() => setViewUnit(null)} />
        )}
        {showPropertyForm && (
          <PropertyForm property={property} onClose={() => setShowPropertyForm(false)} onSaved={handlePropertySaved} />
        )}
        {showDeleteModal && (
          <DeleteConfirmationModal
            show={showDeleteModal}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={handleDeleteConfirm}
            title="Delete Property"
            message={`Are you sure you want to delete "${property?.name}"?`}
          />
        )}
        {showUnitDeleteModal && unitToDelete && (
          <DeleteConfirmationModal
            show={showUnitDeleteModal}
            onClose={() => setShowUnitDeleteModal(false)}
            onConfirm={handleUnitDeleteConfirm}
            title="Delete Unit"
            message={`Are you sure you want to delete Unit #${unitToDelete.unit_number}?`}
          />
        )}
      </div>
    </>
  );
}